/**
 * Release Notes Button
 *
 * A button/icon that shows a badge when there are unseen release notes.
 * Clicking opens the ReleaseNotesDialog.
 */

import React, { useState, useEffect } from "react";
import { Badge, IconButton, Tooltip } from "@mui/material";
import { Campaign as AnnouncementIcon } from "@mui/icons-material";
import { useGetReleaseNotesStatus } from "../../services/ReleaseNotesService";
import {
  useGetUserSettings,
  useUpdateUserSettings,
} from "../../../views/users/SettingsService";
import ReleaseNotesDialog from "./ReleaseNotesDialog";

interface ReleaseNotesButtonProps {
  color?: "inherit" | "default" | "primary" | "secondary";
}

const ReleaseNotesButton: React.FC<ReleaseNotesButtonProps> = ({
  color = "inherit",
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data: status, isSuccess: statusLoaded } = useGetReleaseNotesStatus();

  // Use existing settings system to persist auto-opened state across remounts
  const { data: userSettings, isSuccess: settingsLoaded } =
    useGetUserSettings();
  const updateSettings = useUpdateUserSettings();

  const hasUnseenRelease = status?.has_unseen_release ?? false;
  const autoPopupEnabled = status?.auto_popup_enabled ?? true;
  const currentVersion = status?.current_version;

  // Check if we've already auto-opened for this version (persisted in user settings)
  const autoOpenedVersion =
    userSettings?.release_notes_auto_opened_version ?? "";
  const hasAutoOpenedForVersion = autoOpenedVersion === currentVersion;

  // Auto-open dialog once per session when there are unseen release notes (if enabled)
  useEffect(() => {
    if (
      statusLoaded &&
      settingsLoaded &&
      hasUnseenRelease &&
      autoPopupEnabled &&
      currentVersion &&
      !hasAutoOpenedForVersion
    ) {
      // Persist that we've auto-opened for this version
      updateSettings.mutate({
        release_notes_auto_opened_version: currentVersion,
      });
      setDialogOpen(true);
    }
  }, [
    statusLoaded,
    settingsLoaded,
    hasUnseenRelease,
    autoPopupEnabled,
    currentVersion,
    hasAutoOpenedForVersion,
  ]);

  return (
    <>
      <Tooltip title="What's New">
        <IconButton
          data-testid="hdr-btn-release-notes"
          onClick={() => setDialogOpen(true)}
          color={color}
          size="small"
          aria-label="Release notes"
        >
          <Badge
            color="error"
            variant="dot"
            invisible={!hasUnseenRelease}
            overlap="circular"
          >
            <AnnouncementIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <ReleaseNotesDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};

export default ReleaseNotesButton;
